import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PlusCircle, User, Users2, UserPlus, Filter, Search, Settings } from 'lucide-react';
import { User as UserEntity } from '@/entities/User';
import { StagedUser } from '@/entities/StagedUser';
import { District } from '@/entities/District';
import UserTable from '../components/manage_users/UserTable';
import StagedUserTable from '../components/manage_users/StagedUserTable';
import UserForm from '../components/manage_users/UserForm';
import UserFilters from '../components/manage_users/UserFilters';
import StagedUserFilters from '../components/manage_users/StagedUserFilters';
import InviteUserForm from '../components/manage_users/InviteUserForm';
import UserGroupManager from '../components/manage_users/UserGroupManager';
import AdvancedUserSearch from '../components/manage_users/AdvancedUserSearch';
import { useToast } from '@/hooks/use-toast';
import { manageUserProvisioning } from '@/functions/manageUserProvisioning';
import { toast } from 'sonner';
import { User as UserType, StagedUser as StagedUserType, District as DistrictType } from '@/types';
import { RoleManager } from '../components/manage_users/RoleManager';
import { useRole } from '@/hooks/useRole';
import { supabase } from '@/integrations/supabase/client';
import DemoStaffManager from '../components/manage_users/DemoStaffManager';

export default function ManageUsers() {
  const { isAdmin } = useRole();
  const [isUberAdmin, setIsUberAdmin] = useState(false);
  const [users, setUsers] = useState<UserType[]>([]);
  const [stagedUsers, setStagedUsers] = useState<StagedUserType[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserType[]>([]);
  const [filteredStagedUsers, setFilteredStagedUsers] = useState<StagedUserType[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<UserType | null>(null);
  const [district, setDistrict] = useState<DistrictType | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isInviteFormOpen, setIsInviteFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);
  const { toast: uiToast } = useToast();
  const [currentTab, setCurrentTab] = useState<string>('active_users');

  const loadUsers = useCallback(async (user: UserType) => {
    if (!user) return;
    setLoading(true);
    try {
      // Check if user is uber admin or has admin role
      const { data: isUberAdminCheck, error: uberError } = await supabase.rpc('is_uber_admin');
      const { data: isAdminCheck, error: adminError } = await supabase.rpc('is_admin', { p_user_id: user.id });
      
      console.log('🔍 Admin checks:', {
        userEmail: user.email,
        isUberAdmin: isUberAdminCheck,
        uberError: uberError,
        isDistrictAdmin: isAdminCheck,
        adminError: adminError,
        districtId: user.district_id
      });
      
      let districtUsers: UserType[];
      let districtData: DistrictType | null = null;
      
      // Update state
      setIsUberAdmin(!!isUberAdminCheck);
      
      if (isUberAdminCheck) {
        // Uber admins can see ALL users across ALL districts (including trial users with no district)
        districtUsers = await UserEntity.filter({});
        console.log('✅ Uber admin: loaded all users', districtUsers.length, districtUsers.map(u => ({ email: u.email, district: u.district_id })));
      } else if (isAdminCheck && user.district_id) {
        // Regular district admins see users in their district only
        const allUsers = await UserEntity.filter({});
        districtUsers = allUsers.filter(u => 
          u.district_id === user.district_id
        );
        districtData = await District.get(user.district_id);
        console.log('📍 District admin: loaded district users', districtUsers.length);
      } else {
        console.log('❌ No admin privileges found');
        districtUsers = [];
      }

      // Fetch role information for each user (including expiration)
      const userIds = districtUsers.map(u => u.id);
      const { data: rolesData } = await supabase
        .from('user_roles')
        .select('user_id, role, granted_at, granted_by, expires_at')
        .in('user_id', userIds)
        .eq('is_active', true);

      // Map roles to users
      const usersWithRoles = districtUsers.map(u => {
        const roleData = rolesData?.find(r => r.user_id === u.id);
        return {
          ...u,
          current_role: roleData?.role,
          role_granted_at: roleData?.granted_at,
          role_granted_by: roleData?.granted_by,
          role_expires_at: roleData?.expires_at
        };
      });
      
      setUsers(usersWithRoles);
      setFilteredUsers(usersWithRoles);
      setDistrict(districtData);
    } catch (error) {
      console.error("Error loading users:", error);
      toast.error("Failed to load users.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStagedUsers = useCallback(async (user: UserType) => {
    if (!user || !user.district_id) return;
    setLoading(true);
    try {
      const staged = await StagedUser.filter({ district_id: user.district_id });
      setStagedUsers(staged);
      setFilteredStagedUsers(staged);
    } catch (error) {
      console.error("Error loading staged users:", error);
      toast.error("Failed to load staged users.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const user = await UserEntity.me();
        setCurrentUser(user);
        
        // Check if uber admin
        const { data: uberAdmin } = await supabase.rpc('is_uber_admin');
        setIsUberAdmin(!!uberAdmin);
        
        await loadUsers(user);
        await loadStagedUsers(user);
      } catch (error) {
        console.error("Authentication error:", error);
        toast.error("You must be logged in to manage users.");
      }
    };
    init();
  }, [loadUsers, loadStagedUsers]);

  const handleEditUser = (user: UserType) => {
    setEditingUser(user);
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingUser(null);
    if (currentUser) loadUsers(currentUser);
  };

  const handleInviteFormClose = () => {
    setIsInviteFormOpen(false);
    if (currentUser) loadStagedUsers(currentUser);
  };

  const handleProvisionUser = async (stagedUser: StagedUserType) => {
    try {
      await manageUserProvisioning({ userId: stagedUser.id, action: 'provision' });
      toast.success(`User "${stagedUser.full_name}" has been provisioned.`);
      if (currentUser) {
        await loadStagedUsers(currentUser);
        await loadUsers(currentUser);
      }
    } catch (error) {
      console.error("Error provisioning user:", error);
      toast.error("Failed to provision user.");
    }
  };

  const handleDeleteStagedUser = async (stagedUser: StagedUserType) => {
    try {
      await StagedUser.delete(stagedUser.id);
      toast.success(`Staged user "${stagedUser.full_name}" deleted successfully.`);
      if (currentUser) {
        await loadStagedUsers(currentUser);
      }
    } catch (error) {
      console.error("Error deleting staged user:", error);
      toast.error("Failed to delete staged user.");
    }
  };

  const handleDeleteUser = async (user: UserType) => {
    try {
      // Delete from profiles table (this will cascade to related data)
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.id);

      if (error) throw error;

      toast.success(`User "${user.full_name || `${user.first_name} ${user.last_name}`}" deleted successfully.`);
      if (currentUser) {
        await loadUsers(currentUser);
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error("Failed to delete user. They may have associated records that prevent deletion.");
    }
  };

  const handleSchoolTransfer = async (user: UserType, newSchool: string) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ school_name: newSchool })
        .eq('id', user.id);

      if (error) throw error;

      toast.success(`${user.full_name || `${user.first_name} ${user.last_name}`} transferred to ${newSchool}`);
      if (currentUser) {
        await loadUsers(currentUser);
      }
    } catch (error) {
      console.error("Error transferring user:", error);
      toast.error("Failed to transfer user to new school.");
    }
  };

  const handleBulkDelete = async (users: UserType[]) => {
    try {
      // Delete multiple users from profiles table
      const userIds = users.map(u => u.id);
      const { error } = await supabase
        .from('profiles')
        .delete()
        .in('id', userIds);

      if (error) throw error;

      toast.success(`Successfully deleted ${users.length} user${users.length !== 1 ? 's' : ''}.`);
      if (currentUser) {
        await loadUsers(currentUser);
      }
    } catch (error) {
      console.error("Error deleting users:", error);
      toast.error("Failed to delete selected users. Some users may have associated records that prevent deletion.");
    }
  };
  
  const handleSaveUserGroup = async (groupData: any) => {
    console.log("User Group created/updated:", groupData);
    // Refresh users list if needed
    if (currentUser) {
      loadUsers(currentUser);
    }
  };

  if (loading && !currentUser) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/3"></div>
          <div className="grid grid-cols-1 gap-6">
            <div className="h-64 bg-slate-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-8 max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">User Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage district users and invitations
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setIsInviteFormOpen(true)} className="bg-gradient-primary">
            <UserPlus className="w-4 h-4 mr-2" /> Invite User
          </Button>
          <Button 
            variant="outline" 
            onClick={() => { setEditingUser(null); setIsFormOpen(true); }}
          >
            <PlusCircle className="w-4 h-4 mr-2" /> Add User Manually
          </Button>
          {isAdmin && (
            <Button 
              variant="secondary" 
              onClick={() => setCurrentTab('roles')}
            >
              <Settings className="w-4 h-4 mr-2" /> Manage Roles
            </Button>
          )}
        </div>
      </div>

      <Tabs value={currentTab} onValueChange={setCurrentTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="active_users" className="flex items-center gap-2">
            <Users2 className="w-4 h-4" /> 
            Active ({filteredUsers.length})
          </TabsTrigger>
          <TabsTrigger value="staged_users" className="flex items-center gap-2">
            <User className="w-4 h-4" /> 
            Invited ({filteredStagedUsers.length})
          </TabsTrigger>
          <TabsTrigger value="demo_staff" className="flex items-center gap-2">
            <Users2 className="w-4 h-4" /> 
            Demo Staff
          </TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="roles" className="flex items-center gap-2">
              <Users2 className="w-4 h-4" /> 
              Roles
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="active_users" className="space-y-6">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users2 className="w-5 h-5 text-primary" />
                Active District Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <UserFilters users={users} onFilter={setFilteredUsers} />
              <UserTable 
                users={filteredUsers} 
                onEdit={handleEditUser}
                onDelete={handleDeleteUser}
                onBulkDelete={handleBulkDelete}
                onSchoolTransfer={handleSchoolTransfer}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="staged_users" className="space-y-6">
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Staged & Invited Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <StagedUserFilters users={stagedUsers} onFilter={setFilteredStagedUsers} />
              <StagedUserTable 
                users={filteredStagedUsers} 
                onProvision={handleProvisionUser} 
                onDelete={handleDeleteStagedUser}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="demo_staff" className="space-y-6">
          <DemoStaffManager />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="roles" className="space-y-6">
            <RoleManager 
              users={filteredUsers.map(user => ({
                id: user.id,
                first_name: user.full_name?.split(' ')[0] || '',
                last_name: user.full_name?.split(' ').slice(1).join(' ') || '',
                email: user.email,
                department: user.job_type || '',
                position_title: user.job_title || '',
                school_name: user.school_name || '',
                current_role: (user as any).current_role,
                role_granted_at: (user as any).role_granted_at,
                role_granted_by: (user as any).role_granted_by,
                role_expires_at: (user as any).role_expires_at
              }))}
              onRoleUpdate={() => currentUser && loadUsers(currentUser)}
            />
          </TabsContent>
        )}
      </Tabs>
      
      <Card className="shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-primary" /> 
            Advanced Search & Groups
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="search" className="space-y-4">
            <TabsList>
              <TabsTrigger value="search" className="flex items-center gap-2">
                <Search className="w-4 h-4"/>
                Search
              </TabsTrigger>
              <TabsTrigger value="groups" className="flex items-center gap-2">
                <Users2 className="w-4 h-4"/>
                Groups
              </TabsTrigger>
            </TabsList>
            <TabsContent value="search" className="pt-4">
               <AdvancedUserSearch onSearch={setFilteredUsers} allUsers={users} />
            </TabsContent>
            <TabsContent value="groups" className="pt-4">
              <UserGroupManager districtId={currentUser?.district_id} onSave={handleSaveUserGroup} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <UserForm 
        isOpen={isFormOpen} 
        onClose={handleFormClose} 
        user={editingUser} 
        districtId={currentUser?.district_id}
        allowDistrictChange={isUberAdmin}
      />
      <InviteUserForm 
        isOpen={isInviteFormOpen} 
        onClose={handleInviteFormClose} 
        districtId={currentUser?.district_id} 
      />
    </div>
  );
}